import {
  CustomChartType,
  IBaseChartBuilderConfig,
  IColumnChartBuilderConfig,
  ICustomReportChart,
  MeasureReducer,
} from '@principle-theorem/principle-core/interfaces';
import {
  IChartBuilderData,
  IChartConfig,
  IMeasureBuilderData,
} from '@principle-theorem/reporting/interfaces';
import { generateBuilderData } from '../../models/base-measures';
import { CanGroupMeasuresProperty } from '../../models/measure-properties';

export interface IChartFilterDefinition<
  T extends IBaseChartBuilderConfig = IBaseChartBuilderConfig,
> {
  config: IChartConfig;
  builderConfig?: Partial<T>;
}

export class ChartFilterDefinition {
  static fromChart(
    chart: ICustomReportChart,
    groupBy: CanGroupMeasuresProperty,
    measures: IMeasureBuilderData[]
  ): IChartFilterDefinition | undefined {
    switch (chart.type) {
      case CustomChartType.NumberSummary:
        return ChartFilterDefinition.numberSummary(
          chart.label,
          groupBy,
          measures,
          chart.builderData
        );
      case CustomChartType.Table:
        return ChartFilterDefinition.table(
          chart.label,
          groupBy,
          measures,
          chart.builderData
        );
      case CustomChartType.Column:
        return ChartFilterDefinition.column(
          chart.label,
          groupBy,
          measures,
          chart.builderConfig,
          chart.builderData
        );
      case CustomChartType.Line:
        return ChartFilterDefinition.line(
          chart.label,
          groupBy,
          measures,
          chart.builderConfig,
          chart.builderData
        );
      case CustomChartType.Pie:
        return ChartFilterDefinition.pie(
          chart.label,
          groupBy,
          measures,
          chart.builderData
        );
      case CustomChartType.Row:
        return ChartFilterDefinition.row(
          chart.label,
          groupBy,
          measures,
          chart.builderData
        );
      case CustomChartType.SelectMenu:
        return ChartFilterDefinition.selectMenu(
          chart.label,
          groupBy,
          measures,
          chart.builderData
        );
      default:
        // eslint-disable-next-line no-console
        console.error(`ChartBuilder.get - unknown type "${chart.type}"`);
        return;
    }
  }

  static numberSummary(
    title: string,
    groupBy: CanGroupMeasuresProperty,
    measures: IMeasureBuilderData[],
    builderData?: Partial<IChartBuilderData>
  ): IChartFilterDefinition {
    return {
      config: {
        type: CustomChartType.NumberSummary,
        labels: { title },
        builderData: generateBuilderData({
          ...builderData,
          measures: measures.map((measure) => ({
            ...measure,
            measure: measure.measure
              .clone()
              .groupBy(groupBy)
              .reduceBy(measure?.reducer ?? MeasureReducer.Sum),
          })),
        }),
      },
    };
  }

  static table(
    title: string,
    groupBy: CanGroupMeasuresProperty,
    measures: IMeasureBuilderData[],
    builderData?: Partial<IChartBuilderData>
  ): IChartFilterDefinition {
    return {
      config: {
        type: CustomChartType.Table,
        labels: { title },
        builderData: generateBuilderData({
          ...builderData,
          measures: measures.map((measure) => ({
            ...measure,
            measure: measure.measure
              .clone()
              .groupBy(groupBy)
              .reduceBy(measure.reducer ?? MeasureReducer.Sum),
          })),
          groupByDimension: groupBy,
        }),
      },
    };
  }

  static pie(
    title: string,
    groupBy: CanGroupMeasuresProperty,
    measures: IMeasureBuilderData[],
    builderData?: Partial<IChartBuilderData>
  ): IChartFilterDefinition {
    return {
      config: {
        type: CustomChartType.Pie,
        labels: { title },
        builderData: generateBuilderData({
          ...builderData,
          measures: measures.map((measure) => ({
            ...measure,
            measure: measure.measure
              .clone()
              .groupBy(groupBy)
              .reduceBy(measure?.reducer ?? MeasureReducer.Sum),
          })),
          groupByDimension: groupBy,
        }),
      },
    };
  }

  static row(
    title: string,
    groupBy: CanGroupMeasuresProperty,
    measures: IMeasureBuilderData[],
    builderData?: Partial<IChartBuilderData>
  ): IChartFilterDefinition {
    return {
      config: {
        type: CustomChartType.Row,
        labels: { title },
        builderData: generateBuilderData({
          ...builderData,
          measures: measures.map((measure) => ({
            ...measure,
            measure: measure.measure
              .clone()
              .groupBy(groupBy)
              .reduceBy(measure?.reducer ?? MeasureReducer.Sum),
          })),
          groupByDimension: groupBy,
        }),
      },
    };
  }

  static column(
    title: string,
    groupBy: CanGroupMeasuresProperty,
    measures: IMeasureBuilderData[],
    builderConfig?: Partial<IColumnChartBuilderConfig>,
    builderData?: Partial<IChartBuilderData>
  ): IChartFilterDefinition {
    return {
      builderConfig,
      config: {
        type: CustomChartType.Column,
        labels: { title },
        builderData: generateBuilderData({
          ...builderData,
          measures: measures.map((measure) => ({
            ...measure,
            measure: measure.measure
              .clone()
              .groupBy(groupBy)
              .reduceBy(measure?.reducer ?? MeasureReducer.Sum),
          })),
          groupByDimension: groupBy,
        }),
      },
    };
  }

  static line(
    title: string,
    groupBy: CanGroupMeasuresProperty,
    measures: IMeasureBuilderData[],
    builderConfig?: Partial<IColumnChartBuilderConfig>,
    builderData?: Partial<IChartBuilderData>
  ): IChartFilterDefinition {
    return {
      builderConfig,
      config: {
        type: CustomChartType.Line,
        labels: { title },
        builderData: generateBuilderData({
          ...builderData,
          measures: measures.map((measure) => ({
            ...measure,
            measure: measure.measure
              .clone()
              .groupBy(groupBy)
              .reduceBy(measure?.reducer ?? MeasureReducer.Sum),
          })),
          groupByDimension: groupBy,
        }),
      },
    };
  }

  static columnOverTime(
    title: string,
    groupBy: CanGroupMeasuresProperty,
    measures: IMeasureBuilderData[],
    config?: Partial<IColumnChartBuilderConfig>,
    builderData?: Partial<IChartBuilderData>
  ): IChartFilterDefinition {
    return this.column(title, groupBy, measures, config, {
      ...builderData,
      plottedOverTime: true,
    });
  }

  static selectMenu(
    title: string,
    groupBy: CanGroupMeasuresProperty,
    measures: IMeasureBuilderData[],
    builderData?: Partial<IChartBuilderData>
  ): IChartFilterDefinition {
    return {
      config: {
        type: CustomChartType.SelectMenu,
        labels: { title },
        builderData: generateBuilderData({
          ...builderData,
          measures: measures.map((measure) => ({
            ...measure,
            measure: measure.measure
              .clone()
              .groupBy(groupBy)
              .reduceBy(measure?.reducer ?? MeasureReducer.Sum),
          })),
          groupByDimension: groupBy,
        }),
      },
    };
  }
}
